import { createApi, fetchBaseQuery, BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { getApiConfig } from "@/config/api";
import { getRequestLanguage } from "@/utils/requestLanguage";
import type {
  Item,
  ItemStock,
  ItemUnit,
  ApiResponse,
  PaginatedResponse,
  BackendPaginatedResponse,
  StockAdjustment,
} from "@/types";

// Helper to populate etalase from itemOutletEtalases array
function transformItem(item: Item): Item {
  if (item.itemOutletEtalases && item.itemOutletEtalases.length > 0) {
    return {
      ...item,
      etalase: item.itemOutletEtalases[0].etalase,
    };
  }
  return item;
}

export interface ItemsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  categoryId?: string;
  subcategoryId?: string;
  isActive?: boolean;
  includeInactive?: boolean;
  stockStatus?: "all" | "low" | "out";
  sortBy?: "name" | "sku" | "currentStock" | "sellingPrice" | "expiryDate" | "createdAt";
  sortOrder?: "asc" | "desc";
  storeId?: string;
  outletId?: string;
  etalaseId?: string;
  distributorId?: string;
}

export interface CreateItemData {
  storeId: string; // Required by Backend
  name: string;
  sku?: string;
  barcode?: string;
  categoryId: string;
  subcategoryId?: string;
  distributorId?: string;
  unit: string;
  description?: string;
  image?: string;
  lowStockThreshold: number;
  isActive: boolean;
  allowPriceEdit?: boolean;
  alwaysNewLine?: boolean;
  // Initial stock entry
  initialStock?: {
    outletId: string; // Required if initialStock is present
    quantity: number;
    costPrice: number;
    sellingPrice: number;
    batchCode?: string;
    expiryDate?: string;
  };
}

export interface UpdateItemData {
  name?: string;
  sku?: string;
  barcode?: string;
  categoryId?: string;
  subcategoryId?: string;
  distributorId?: string | null;
  unit?: string;
  description?: string;
  image?: string | null; // Allow null to remove image
  lowStockThreshold?: number;
  isActive?: boolean;
  allowPriceEdit?: boolean;
  alwaysNewLine?: boolean;
}

export interface StockAdjustmentData {
  storeId: string;
  outletId: string;
  itemId: string;
  type: "ADD" | "REMOVE" | "SET";
  quantity: number;
  reason: string;
  note?: string;
  costPrice?: number;
  sellingPrice?: number;
  batchCode?: string;
  expiryDate?: string;
}

export interface ItemStockQueryParams {
  itemId: string;
  page?: number;
  limit?: number;
  hideEmpty?: boolean;
  outletId?: string;
}

export interface BulkImportData {
  file: File;
  templateType: "basic" | "advanced";
}

export interface BulkExportParams {
  format: "csv" | "excel" | "pdf";
  includeStock?: boolean;
  categoryId?: string;
  distributorId?: string;
  isActive?: boolean;
}

export interface InventoryStats {
  totalItems: number;
  activeItems?: number;
  inactiveItems?: number;
  totalValue: number;
  totalRetailValue?: number;
  totalCostValue?: number;
  lowStockItems: number;
  outOfStockItems: number;
  recentActivity?: number;
  recentlyAddedItems?: number;
  topCategories?: Array<{
    id: string;
    name: string;
    itemCount: number;
    totalValue: number;
  }>;
}

export interface LowStockItem {
  id: string;
  name: string;
  sku?: string;
  currentStock: number;
  lowStockThreshold: number;
  unit: string;
  category: {
    name: string;
  };
}

// Create a dynamic base query for inventory API
const dynamicInventoryBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as any).auth.token;
      if (!token) {
        token = localStorage.getItem("token");
      }
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      // Tell the server which app language is selected (exports and templates are localized)
      headers.set("accept-language", getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const inventoryApi = createApi({
  reducerPath: "inventoryApi",
  baseQuery: dynamicInventoryBaseQuery,
  tagTypes: [
    "Item",
    "Category",
    "Subcategory",
    "ItemStock",
    "InventoryStats",
    "ItemUnit",
    "StockHistory",
  ],
  endpoints: builder => ({
    // Get inventory stats
    getInventoryStats: builder.query<
      ApiResponse<InventoryStats>,
      { storeId?: string; outletId?: string } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params) {
          if (params.storeId) queryParams.append("storeId", params.storeId);
          if (params.outletId) queryParams.append("outletId", params.outletId);
        }
        const queryString = queryParams.toString();
        return `/inventory/stats${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["InventoryStats"],
    }),

    // Get all items with filtering and pagination
    getItems: builder.query<ApiResponse<BackendPaginatedResponse<Item>>, ItemsQueryParams>({
      query: params => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            searchParams.append(key, value.toString());
          }
        });
        return `/items?${searchParams.toString()}`;
      },
      transformResponse: (response: ApiResponse<BackendPaginatedResponse<Item>>) => {
        if (response.data?.items) {
          return {
            ...response,
            data: {
              ...response.data,
              items: response.data.items.map(transformItem),
            },
          };
        }
        return response;
      },
      providesTags: result =>
        result && result.data?.items && Array.isArray(result.data.items)
          ? [...result.data.items.map(({ id }) => ({ type: "Item" as const, id })), "Item"]
          : ["Item"],
    }),

    // Get single item
    getItem: builder.query<ApiResponse<Item>, { id: string; outletId?: string }>({
      query: ({ id, outletId }) => {
        const params = new URLSearchParams();
        if (outletId) params.append("outletId", outletId);
        return `/items/${id}?${params.toString()}`;
      },
      transformResponse: (response: ApiResponse<Item>) => {
        if (response.data) {
          return {
            ...response,
            data: transformItem(response.data),
          };
        }
        return response;
      },
      providesTags: (_, __, { id }) => [{ type: "Item", id }],
    }),

    // Create new item
    createItem: builder.mutation<ApiResponse<Item>, CreateItemData>({
      query: data => ({
        url: "/items",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Item", "Category", "Subcategory", "InventoryStats"],
    }),

    // Update item
    updateItem: builder.mutation<ApiResponse<Item>, { id: string; data: UpdateItemData }>({
      query: ({ id, data }) => ({
        url: `/items/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Item", id },
        "Item",
        { type: "ItemUnit", id }, // Invalidate item units when item is updated (especially for base unit changes)
        "Category",
        "Subcategory",
        "InventoryStats",
      ],
    }),

    // Set etalase for item in specific outlet
    setItemEtalase: builder.mutation<ApiResponse<void>, { id: string; etalaseId: string | null; outletId?: string }>({
      query: ({ id, etalaseId, outletId }) => ({
        url: `/items/${id}/etalase`,
        method: "PUT",
        body: { etalaseId, outletId },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Item", id },
        "Item",
      ],
    }),

    // Delete item
    deleteItem: builder.mutation<ApiResponse<void>, { id: string; force?: boolean }>({
      query: ({ id, force }) => ({
        url: `/items/${id}`,
        method: "DELETE",
        body: force ? { force } : undefined,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Item", id },
        "Item",
        "Category",
        "Subcategory",
        "InventoryStats",
      ],
    }),

    // Bulk delete items
    bulkDeleteItems: builder.mutation<ApiResponse<void>, string[]>({
      query: ids => ({
        url: "/items/bulk-delete",
        method: "POST",
        body: { ids },
      }),
      invalidatesTags: (_result, _error, ids) => [
        ...ids.map(id => ({ type: "Item" as const, id })),
        "Item",
        "Category",
        "Subcategory",
        "InventoryStats",
      ],
    }),

    // Get low stock items
    getLowStockItems: builder.query<ApiResponse<LowStockItem[]>, void>({
      query: () => "/items/low-stock",
      providesTags: ["Item"],
    }),

    restockItem: builder.mutation<
      ApiResponse<void>,
      { outletId: string; itemId: string; quantity: number; price: number; cost?: number; batchCode?: string; storeId?: string; reason?: string; note?: string; expiryDate?: string | null }
    >({
      query: ({ itemId, outletId, quantity, price, cost, batchCode, storeId, reason, note, expiryDate }) => ({
        url: "/inventory/stock-in",
        method: "POST",
        body: {
          itemId,
          outletId,
          quantity,
          price,
          cost: cost || price,
          batchCode: batchCode || `BATCH-${Date.now()}`,
          batchSource: "Manual Restock",
          storeId,
          reason,
          notes: note || undefined,
          expiryDate: expiryDate || undefined,
        },
      }),
      invalidatesTags: (_result, _error, { itemId }) => [
        "Item",
        "InventoryStats",
        "ItemStock",
        { type: "StockHistory", id: itemId },
        "StockHistory",
      ],
    }),

    // Stock management
    getItemStocks: builder.query<ApiResponse<PaginatedResponse<ItemStock>>, ItemStockQueryParams>({
      query: ({ itemId, page = 1, limit = 10, hideEmpty = false, outletId }) => ({
        url: `/items/${itemId}/stocks`,
        params: {
          page,
          limit,
          hideEmpty,
          outletId,
        },
      }),
      providesTags: (_result, _error, args) =>
        args?.itemId
          ? [{ type: "ItemStock", id: args.itemId }]
          : [{ type: "ItemStock", id: "LIST" }],
    }),

    // Update a specific stock batch
    updateItemStock: builder.mutation<
      ApiResponse<ItemStock>,
      {
        stockId: string;
        data: { quantity?: number; remainingQuantity?: number; cost?: number; price?: number; expiryDate?: string | null };
      }
    >({
      query: ({ stockId, data }) => ({
        url: `/items/stocks/${stockId}`,
        method: "PUT",
        body: data,
      }),
      // Also invalidate ItemUnit so Multi-Unit pricing reflects latest base unit price
      invalidatesTags: result => {
        const itemId = result?.data?.itemId;
        return [
          "Item",
          "ItemStock",
          "InventoryStats",
          "ItemUnit",
          "StockHistory",
          itemId ? { type: "Item", id: itemId } : undefined,
          itemId ? { type: "ItemStock", id: itemId } : undefined,
          itemId ? { type: "StockHistory", id: itemId } : undefined,
        ].filter(Boolean) as any;
      },
    }),

    // Get item by barcode
    getItemByBarcode: builder.query<ApiResponse<Item>, string>({
      query: barcode => `/items/barcode/${encodeURIComponent(barcode)}`,
      providesTags: result => (result?.data ? [{ type: "Item", id: result.data.id }] : []),
    }),

    // Adjust stock (comprehensive - supports ADD, REMOVE, SET)
    adjustStock: builder.mutation<ApiResponse<void>, StockAdjustmentData>({
      query: data => ({
        url: "/inventory/adjustment",
        method: "POST",
        body: {
          storeId: data.storeId,
          itemId: data.itemId,
          outletId: data.outletId,
          quantityChange:
            data.type === "SET"
              ? data.quantity // For SET, quantity is the target amount
              : data.type === "ADD"
                ? data.quantity
                : -data.quantity, // For REMOVE, make quantity negative
          reason: data.reason,
          batchCode: data.batchCode,
          notes: data.note || undefined,
        },
      }),
      invalidatesTags: (_result, _error, { itemId }) => [
        "Item",
        "ItemStock",
        "InventoryStats",
        { type: "StockHistory", id: itemId },
        "StockHistory",
      ],
    }),

    // Remove stock (specific endpoint for stock removal)
    removeStock: builder.mutation<
      ApiResponse<void>,
      {
        storeId: string;
        outletId: string;
        itemId: string;
        quantity: number;
        reason: string;
        note?: string;
      }
    >({
      query: data => ({
        url: "/inventory/adjustment",
        method: "POST",
        body: {
          storeId: data.storeId,
          itemId: data.itemId,
          outletId: data.outletId,
          quantityChange: -data.quantity, // Negative for removal
          reason: data.reason,
          notes: data.note,
        },
      }),
      invalidatesTags: (_result, _error, { itemId }) => [
        "Item",
        "ItemStock",
        "InventoryStats",
        { type: "StockHistory", id: itemId },
        "StockHistory",
      ],
    }),

    // Set stock to specific level
    setStock: builder.mutation<
      ApiResponse<void>,
      {
        storeId: string;
        outletId: string;
        itemId: string;
        targetQuantity: number;
        reason: string;
        note?: string;
      }
    >({
      query: data => ({
        url: "/inventory/set-stock",
        method: "POST",
        body: {
          storeId: data.storeId,
          itemId: data.itemId,
          outletId: data.outletId,
          targetQuantity: data.targetQuantity,
          reason: data.reason,
          notes: data.note,
        },
      }),
      invalidatesTags: (_result, _error, { itemId }) => [
        "Item",
        "ItemStock",
        "InventoryStats",
        { type: "StockHistory", id: itemId },
        "StockHistory",
      ],
    }),

    // Get adjustment reasons
    getAdjustmentReasons: builder.query<
      ApiResponse<{ ADD: string[]; REMOVE: string[]; SET: string[] }>,
      { storeId: string }
    >({
      query: ({ storeId }) => `/inventory/adjustment-reasons?storeId=${storeId}`,
      providesTags: ["StockHistory"], // Re-fetch when stock history changes (which happens on adjustment)
    }),

    // Get stock adjustments history
    getStockHistory: builder.query<
      ApiResponse<BackendPaginatedResponse<StockAdjustment>>,
      { itemId?: string; outletId?: string; page?: number; limit?: number }
    >({
      query: params => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            searchParams.append(key, value.toString());
          }
        });
        return `/stock-adjustments/adjustments?${searchParams.toString()}`;
      },
      providesTags: (_result, _error, params) => {
        const tags: Array<{ type: "StockHistory"; id?: string } | "StockHistory"> = [
          "StockHistory",
        ];
        if (params.itemId) {
          tags.push({ type: "StockHistory", id: params.itemId });
        }
        return tags;
      },
    }),

    // Bulk operations
    bulkImport: builder.mutation<
      ApiResponse<{
        imported: number;
        failed: number;
        errors: Array<{ row: number; error: string }>;
      }>,
      FormData
    >({
      query: formData => ({
        url: "/items/bulk-import",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Item", "Category", "Subcategory", "InventoryStats", "StockHistory"],
    }),

    // Export items (mutations don't cache by default, so blob is safe)
    exportItems: builder.mutation<Blob, BulkExportParams>({
      query: params => ({
        url: "/items/export",
        method: "POST",
        body: params,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // Get import template (advanced only)
    getImportTemplate: builder.query<Blob, void>({
      query: () => ({
        url: `/items/import-template`,
        responseHandler: (response: Response) => response.blob(),
      }),
      keepUnusedDataFor: 0,
    }),

    // Upload item image
    uploadItemImage: builder.mutation<
      ApiResponse<{
        id: string;
        filename: string;
        path: string;
        size: number;
        mimetype: string;
      }>,
      { itemId: string; image: File }
    >({
      query: ({ itemId, image }) => {
        const formData = new FormData();
        formData.append("image", image);
        return {
          url: `/items/${itemId}/upload-image`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_, __, { itemId }) => [{ type: "Item", id: itemId }, "Item"],
    }),

    // Update bulk prices
    updateBulkPrices: builder.mutation<
      ApiResponse<{ updated: number }>,
      {
        itemIds: string[];
        adjustmentType: "fixed" | "percentage";
        priceType: "selling" | "cost" | "both";
        operationType: "increase" | "decrease" | "set";
        batchScope: "latest" | "all";
        value: number;
      }
    >({
      query: data => ({
        url: "/items/bulk-update-prices",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Item", "InventoryStats"],
    }),

    // Update bulk status
    bulkUpdateStatus: builder.mutation<
      ApiResponse<{ updated: number }>,
      { itemIds: string[]; isActive: boolean }
    >({
      query: data => ({
        url: "/items/bulk-update-status",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Item", "InventoryStats"],
    }),

    // Multi-Unit Pricing Endpoints
    getItemUnits: builder.query<ApiResponse<ItemUnit[]>, string>({
      query: itemId => `/items/${itemId}/units`,
      providesTags: (_result, _error, itemId) => [{ type: "ItemUnit", id: itemId }],
    }),

    createItemUnit: builder.mutation<
      ApiResponse<ItemUnit>,
      {
        itemId: string;
        unitName: string;
        conversionFactor: number;
        sellPrice: number;
        priority?: number;
      }
    >({
      query: ({ itemId, ...data }) => ({
        url: `/items/${itemId}/units`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { itemId }) => [{ type: "ItemUnit", id: itemId }],
    }),

    updateItemUnit: builder.mutation<
      ApiResponse<ItemUnit>,
      {
        unitId: string;
        unitName?: string;
        conversionFactor?: number;
        sellPrice?: number;
        isActive?: boolean;
        priority?: number;
      }
    >({
      query: ({ unitId, ...data }) => ({
        url: `/items/units/${unitId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["ItemUnit"],
    }),

    deleteItemUnit: builder.mutation<ApiResponse<void>, string>({
      query: unitId => ({
        url: `/items/units/${unitId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ItemUnit"],
    }),

    setBaseUnit: builder.mutation<ApiResponse<{ units: ItemUnit[]; item: Item }>, string>({
      query: unitId => ({
        url: `/items/units/${unitId}/set-base`,
        method: "PUT",
      }),
      invalidatesTags: result => {
        const itemId = result?.data?.item?.id;
        return [
          "ItemUnit",
          "Item",
          "StockHistory",
          "ItemStock",
          itemId ? { type: "Item", id: itemId } : undefined,
          itemId ? { type: "StockHistory", id: itemId } : undefined,
          itemId ? { type: "ItemUnit", id: itemId } : undefined,
          itemId ? { type: "ItemStock", id: itemId } : undefined,
        ].filter(Boolean) as any;
      },
    }),

    // Download expiry update template
    downloadExpiryTemplate: builder.mutation<Blob, { storeId: string; outletId?: string }>({
      query: ({ storeId, outletId }) => {
        const params = new URLSearchParams({ storeId });
        if (outletId) params.append("outletId", outletId);
        return {
          url: `/inventory/bulk/expiry-template?${params.toString()}`,
          method: "GET",
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),

    // Bulk update expiry dates from Excel
    bulkUpdateExpiry: builder.mutation<
      ApiResponse<{ updated: number; failed: number; errors: Array<{ row: number; error: string }> }>,
      FormData
    >({
      query: (formData) => ({
        url: "/inventory/bulk/update-expiry",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["ItemStock", "Item"],
    }),
  }),
});

export const {
  useGetInventoryStatsQuery,
  useGetItemsQuery,
  useLazyGetItemsQuery,
  useGetItemQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useSetItemEtalaseMutation,
  useDeleteItemMutation,
  useBulkDeleteItemsMutation,
  useGetLowStockItemsQuery,
  useRestockItemMutation,
  useGetItemStocksQuery,
  useGetItemByBarcodeQuery,
  useAdjustStockMutation,
  useRemoveStockMutation,
  useSetStockMutation,
  useGetStockHistoryQuery,
  useGetAdjustmentReasonsQuery,
  useBulkImportMutation,
  useExportItemsMutation,
  useGetImportTemplateQuery,
  useUploadItemImageMutation,
  useUpdateBulkPricesMutation,
  useBulkUpdateStatusMutation,
  useGetItemUnitsQuery,
  useCreateItemUnitMutation,
  useUpdateItemUnitMutation,
  useDeleteItemUnitMutation,
  useSetBaseUnitMutation,
  useUpdateItemStockMutation,
  useDownloadExpiryTemplateMutation,
  useBulkUpdateExpiryMutation,
} = inventoryApi;
